import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from '~/hooks';
import { getPartnerAccounts } from '~/services/userAPI/accountsService';
import { useCurrentOrganization } from '../organization';
import { validateApiResponse } from '../utils';

export const getPartnerAccountsQueryKey = () => ['partnerAccounts'];

export const usePartnerAccountsQuery = () => {
  const currentUser = useSelector(({ userResources }) => userResources.users.current);

  return useQuery(
    getPartnerAccountsQueryKey(),
    async () => {
      const response = validateApiResponse(await getPartnerAccounts(currentUser.id));
      return response.data;
    },
    {
      enabled: !!currentUser,
    },
  );
};

// Normally, a single user should have access to only one account. So just pick the first one returned.
export const useCurrentPartnerAccount = () => {
  const { data: currentOrganization } = useCurrentOrganization();
  const { data, ...query } = usePartnerAccountsQuery();

  const partnerAccount = useMemo(() => {
    if (!currentOrganization) return null;
    if (!data || !data.length) return null;
    return data.find(({ id }) => id === currentOrganization.account_id);
  }, [data, currentOrganization]);

  return { data: partnerAccount, ...query };
};

export const useCurrentPartnerAccountUser = () => {
  const currentUser = useSelector((state) => state.userResources.users.current);
  const { data, ...query } = useCurrentPartnerAccount();

  const accountUser = useMemo(() => {
    return data?.users?.find((user) => user.id === currentUser.id) ?? null;
  }, [currentUser?.id, data?.users]);

  return { data: accountUser, ...query };
};
